import { useEffect, useState } from "react";
import { Check, Copy, Cpu, KeyRound, Loader2, Plus, UserPlus, X } from "lucide-react";
import { api, localTime, type AgentStatus } from "../lib/api";
import { useApp } from "../App";

type U = { id: number; email: string; name: string; role: "admin" | "anvandare"; must_change: number; active: number; created_at: string; last_login: string | null };

export function SettingsPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-10 pt-8">
      <Users />
      <AutoAnalysis />
      <Agent />
    </div>
  );
}

/** Visar ett hemligt värde en enda gång, med kopieringsknapp. */
function Secret({ title, value, children, onClose }: { title: string; value: string; children?: React.ReactNode; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-xl border border-ok/30 bg-ok-soft p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="font-medium text-ok">{title}</div>
        <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Stäng"><X size={16} /></button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 break-all rounded-lg bg-card px-3 py-2 font-mono text-[13px]">{value}</code>
        <button className="btn shrink-0" onClick={async () => { await navigator.clipboard.writeText(value); setCopied(true); }}>
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Kopierat" : "Kopiera"}
        </button>
      </div>
      {children}
    </div>
  );
}

function Users() {
  const { user: me } = useApp();
  const [users, setUsers] = useState<U[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "anvandare" as U["role"] });
  const [secret, setSecret] = useState<{ who: string; pw: string } | null>(null);
  const [err, setErr] = useState("");
  const load = () => api<U[]>("users").then(setUsers);
  useEffect(() => { load(); }, []);

  const patch = async (u: U, body: object) => {
    setErr("");
    try {
      const r = await api<{ tempPassword?: string }>(`users/${u.id}`, { method: "PATCH", json: body });
      if (r.tempPassword) setSecret({ who: u.name, pw: r.tempPassword });
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : "Fel"); }
  };

  return (
    <section>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Användare</h1>
          <p className="mt-1 text-soft"><b>Admin</b> laddar upp, taggar och publicerar. <b>Användare</b> ser publicerade bilder, laddar ner och exporterar.</p>
        </div>
        {!adding && <button className="btn-primary shrink-0" onClick={() => setAdding(true)}><UserPlus size={15} /> Lägg till</button>}
      </div>

      {secret && (
        <div className="mb-4">
          <Secret title={`Tillfälligt lösenord för ${secret.who}`} value={secret.pw} onClose={() => setSecret(null)}>
            <p className="mt-2 text-[13px] text-soft">Lämna över det personligen eller via Teams – visas bara nu. Personen väljer ett eget lösenord vid första inloggningen.</p>
          </Secret>
        </div>
      )}

      {adding && (
        <form className="card mb-4 grid gap-3 p-4 sm:grid-cols-[1fr_1fr_160px_auto]" onSubmit={async (e) => {
          e.preventDefault(); setErr("");
          try {
            const r = await api<{ tempPassword: string }>("users", { method: "POST", json: form });
            setSecret({ who: form.name, pw: r.tempPassword });
            setForm({ name: "", email: "", role: "anvandare" }); setAdding(false); load();
          } catch (x) { setErr(x instanceof Error ? x.message : "Fel"); }
        }}>
          <input className="field" placeholder="Namn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <input className="field" type="email" placeholder="namn@smsparbank.se" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select className="field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as U["role"] })}>
            <option value="anvandare">Användare</option>
            <option value="admin">Admin</option>
          </select>
          <div className="flex gap-2">
            <button className="btn-primary"><Plus size={15} /> Skapa</button>
            <button type="button" className="btn" onClick={() => setAdding(false)}>Avbryt</button>
          </div>
        </form>
      )}
      {err && <p className="mb-3 text-bad">{err}</p>}

      <div className="card divide-y divide-line">
        {users.map((u) => (
          <div key={u.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${u.active ? "" : "opacity-50"}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium">{u.name} {u.id === me?.id && <span className="text-[12px] font-normal text-muted">(du)</span>}</div>
              <div className="truncate text-[12.5px] text-muted">
                {u.email} · {!u.active ? "avstängd" : u.must_change ? "har inte loggat in än" : u.last_login ? `senast inne ${localTime(u.last_login, false)}` : "aldrig inloggad"}
              </div>
            </div>
            <select className="rounded-md border border-line bg-card px-2 py-1 text-[13px]" value={u.role} disabled={u.id === me?.id}
              onChange={(e) => patch(u, { role: e.target.value })}>
              <option value="anvandare">Användare</option>
              <option value="admin">Admin</option>
            </select>
            {u.id !== me?.id && (
              <>
                <button className="btn !py-1 text-[13px]" onClick={() => patch(u, { resetPassword: true })}><KeyRound size={13} /> Nytt lösenord</button>
                <button className="btn !py-1 text-[13px]" onClick={() => patch(u, { active: !u.active })}>{u.active ? "Stäng av" : "Aktivera"}</button>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function AutoAnalysis() {
  const { status } = useApp();
  const [s, setS] = useState<{ autoVision: boolean; autoClaude: boolean } | null>(null);
  useEffect(() => { api<{ autoVision: boolean; autoClaude: boolean }>("settings").then(setS); }, []);
  const set = async (k: "autoVision" | "autoClaude", v: boolean) => setS(await api("settings", { method: "PATCH", json: { [k]: v } }));
  const Toggle = ({ k, label, text, enabled }: { k: "autoVision" | "autoClaude"; label: string; text: string; enabled: boolean }) => (
    <label className={`flex items-start gap-3 px-4 py-3 ${enabled ? "cursor-pointer" : "opacity-50"}`}>
      <input type="checkbox" className="mt-1 accent-ember" disabled={!enabled || !s} checked={!!s?.[k]} onChange={(e) => set(k, e.target.checked)} />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-[13px] text-soft">{text}</div>
      </div>
    </label>
  );
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight">Analys av nya bilder</h2>
      <p className="mt-1 text-soft">
        Normalt väljer ni själva vilka bilder som ska AI-analyseras: markera bilder och klicka på <b>Analysera</b>.
        Här kan ni i stället låta varje ny uppladdning analyseras direkt. Filinfo och GPS läses alltid, och det stannar på servern.
      </p>
      <div className="card mt-4 divide-y divide-line">
        <Toggle k="autoVision" label="Apple Vision automatiskt" enabled={!!status?.ai.vision}
          text="Varje ny bild köas till Mac-agenten. Bilden lämnar inte banken." />
        <Toggle k="autoClaude" label="Claude automatiskt" enabled={!!status?.ai.claude}
          text={status?.ai.claude ? "Varje ny bild skickas till Anthropic för analys." : "Kräver ANTHROPIC_API_KEY på servern."} />
      </div>
    </section>
  );
}

function Agent() {
  const { status } = useApp();
  const [s, setS] = useState<AgentStatus | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api<AgentStatus>("agents").then(setS);
  useEffect(() => { load(); const t = setInterval(load, 10_000); return () => clearInterval(t); }, []);
  const server = location.origin;
  const mode = status?.ai.visionMode;

  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight">Mac-agent</h2>
      <p className="mt-1 text-soft">
        Apple Vision ger taggförslag, hittar ansikten och läser text i bilderna. Analysen körs på en admin-Mac, inte på servern:
        agenten hämtar nya bilder, analyserar dem lokalt och skickar bara tillbaka resultatet.
      </p>
      {mode === "local" && (
        <p className="mt-3 rounded-lg bg-mist px-3 py-2 text-[13px] text-soft">
          Servern körs just nu på en Mac och kör Apple Vision själv, så ingen agent behövs. I molnet (eller med
          <code className="mx-1">BILDBANKEN_VISION=agent</code>) tar agenten över.
        </p>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Stat label="Status" value={s ? (s.online ? "Online" : s.agents.length ? "Offline" : "Ingen agent") : "…"} tone={s?.online ? "ok" : "warn"} />
        <Stat label="Väntar på analys" value={s ? String(s.waiting) : "…"} />
        <Stat label="Analyserade totalt" value={s ? String(s.agents.reduce((n, a) => n + a.analysed, 0)) : "…"} />
      </div>

      {s && s.agents.length > 0 && (
        <div className="card mt-4 divide-y divide-line">
          {s.agents.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className={`h-2 w-2 rounded-full ${a.online ? "bg-ok" : "bg-muted"}`} />
              <Cpu size={15} className="text-muted" />
              <div className="flex-1">
                <div className="font-medium">{a.name}</div>
                <div className="text-[12px] text-muted">{a.last_seen ? `senast hörd ${localTime(a.last_seen)}` : "har aldrig anslutit"} · {a.analysed} bilder</div>
              </div>
              <button className="text-[13px] text-soft hover:text-bad" onClick={async () => {
                if (confirm(`Koppla bort ”${a.name}”? Agenten slutar fungera direkt.`)) { await api(`agents/${a.id}`, { method: "DELETE" }); load(); }
              }}>Koppla bort</button>
            </div>
          ))}
        </div>
      )}

      {token ? (
        <div className="mt-4">
          <Secret title="Agent-token – visas bara nu" value={token} onClose={() => setToken(null)}>
            <div className="mt-3 space-y-2 text-[13px] text-soft">
              <p>Kör på Macen (kräver <a className="underline" href="https://bun.sh" target="_blank">Bun</a> och Xcode Command Line Tools), i Bildbankens projektmapp:</p>
              <pre className="overflow-x-auto rounded-lg bg-ink p-3 font-mono text-[12px] text-stone-200">{`bun agent/bildbanken-agent.ts koppla ${server} ${token}
bun agent/bildbanken-agent.ts            # kör nu
bun agent/bildbanken-agent.ts autostart  # starta automatiskt vid inloggning`}</pre>
            </div>
          </Secret>
        </div>
      ) : (
        <form className="mt-4 flex gap-2" onSubmit={async (e) => {
          e.preventDefault(); setBusy(true);
          try { const r = await api<{ token: string }>("agents", { method: "POST", json: { name: name || "Mac" } }); setToken(r.token); setName(""); load(); }
          finally { setBusy(false); }
        }}>
          <input className="field !w-64" placeholder="Namn, t.ex. ”Robins MacBook”" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn-primary" disabled={busy}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Anslut en Mac</button>
        </form>
      )}
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div className="card p-4">
      <div className="text-[12px] text-muted">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold ${tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : ""}`}>{value}</div>
    </div>
  );
}
